import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { isEqual } from 'lodash';
import { auth } from '../firebase';
import { RootState } from '../store';
import { ProductSearchQueryArgs, SuggestionsQuery, SuggestionsSearchResponse } from './models';
import { calculateSearchResultStats } from './models-transformers';

export interface ApiState {
  queries: Record<string, SuggestionsQuery>;
}

export const suggestionsApi = createApi({
  reducerPath: 'suggestionsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/suggestions',
    prepareHeaders: async (headers) => {
      // Add the Authorization header dynamically before each request
      const token = auth.currentUser ? await auth.currentUser.getIdToken(false) : null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    suggestProducts: builder.query<SuggestionsSearchResponse, ProductSearchQueryArgs>({
      query: (productSearchQuery: ProductSearchQueryArgs) => {
        const params = [];
        if (!!productSearchQuery.q) {
          params.push(`q=${productSearchQuery.q}`);
        }
        if (productSearchQuery.limit) {
          params.push(`limit=${productSearchQuery.limit}`);
        }
        if (productSearchQuery.shop_ids && productSearchQuery.shop_ids.length) {
          params.push(productSearchQuery.shop_ids.map(shop_id => `shop_ids=${shop_id}`).join("&"));
        }
        if (productSearchQuery.category_ids && productSearchQuery.category_ids.length) {
          params.push(productSearchQuery.category_ids.map(category_id => `category_ids=${category_id}`).join("&"));
        }
        if (params.length) {
          return ["", params.join("&")].join("?");
        }
        return "";
      },
      transformResponse: (response: SuggestionsSearchResponse): SuggestionsSearchResponse => {
        // Add calculated fields to each ProductSearchResult
        const stats = calculateSearchResultStats(response?.results || [])
        return {
          ...response,
          stats: stats,
        };
      },            
    }),
  }),
});

export const suggestProducts = createAsyncThunk<
  void,
  ProductSearchQueryArgs,
  {
    rejectValue: Error;
  }
>(
  'suggestionsApi/search',
  async (productSearchQuery: ProductSearchQueryArgs, thunkAPI) => {
    await thunkAPI.dispatch(suggestionsApi.endpoints.suggestProducts.initiate(productSearchQuery));
  }
);

export const selectSlice = (state: any): ApiState => state.suggestionsApi;

// all product search queries
export const selectProductSuggestionsQueries = (
  state: RootState,
): SuggestionsQuery[] | null => {
  const ret: SuggestionsQuery[] | undefined = Object.values(selectSlice(state).queries);
  return ret || [];
};

export const selectProductSuggestionsQuery = (
  state: RootState,
  productSearchQueryArgs: ProductSearchQueryArgs | null
): SuggestionsQuery | null => {
  const ret: SuggestionsQuery | undefined = Object.values(selectSlice(state).queries).find(
    (item) =>
      item.endpointName === 'suggestProducts' &&
      isEqual(item.originalArgs, productSearchQueryArgs));
  return ret || null;
};
